import { Link } from 'react-router-dom';
import { Heart, Star, ShoppingBag, Check, ChevronDown, Award, Percent, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useBestsellers } from '../context/BestsellerContext';
import { toast } from 'sonner';
import { QuickViewButton } from './QuickViewModal';
import { OptimizedImage } from './OptimizedImage';
import { useState, useRef, useEffect } from 'react';

const safePrice = (v) => { const n = Number(v); return Number.isFinite(n) ? n.toFixed(2) : '0.00'; };

/* ─── Helpers ─── */

/** Strip leading brand name from product title to avoid duplication */
const stripBrand = (name, brand) => {
  if (!brand || !name) return name || '';
  const re = new RegExp(`^${brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[-–:]?\\s*`, 'i');
  const cleaned = name.replace(re, '');
  return cleaned || name;
};

/**
 * Strip trailing colour from product title when it matches a value in the
 * product's `colors` array. Handles trailing dash/hyphen separators too.
 * Only removes when it's clearly a suffix — not embedded in real product words.
 *
 * "Supersoft Longline Sweatshirt Black" + colors=["Black"] → "Supersoft Longline Sweatshirt"
 * "Coquette Bow Keyring - Brown" + colors=["Brown"] → "Coquette Bow Keyring"
 * "Black Leopard Leggings" + colors=["Black"] → "Black Leopard Leggings" (colour is NOT a suffix)
 * "Deluxe Self-Care Journal – Beige" + colors=["Beige"] → "Deluxe Self-Care Journal"
 */
const stripTrailingColour = (title, colors) => {
  if (!colors?.length || !title) return title;
  let result = title;
  for (const colour of colors) {
    if (!colour) continue;
    // Escape regex special chars in colour name
    const esc = colour.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Match: optional separator (space, dash, en-dash, em-dash) then the colour at end of string
    const re = new RegExp(`\\s*[-–—]?\\s*${esc}\\s*$`, 'i');
    const match = result.match(re);
    if (match) {
      const stripped = result.slice(0, match.index).trim();
      // Safety: only strip if remaining title still has meaningful content (≥3 chars)
      // and the colour was truly at the end, not part of a compound word
      if (stripped.length >= 3) {
        result = stripped;
        break; // Only strip one colour match
      }
    }
  }
  return result;
};

/** Also strip trailing parenthetical colour like "(Light Brown)" */
const stripParenColour = (title, colors) => {
  if (!colors?.length || !title) return title;
  for (const colour of colors) {
    if (!colour) continue;
    const esc = colour.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`\\s*\\(${esc}\\)\\s*$`, 'i');
    const match = title.match(re);
    if (match) {
      const stripped = title.slice(0, match.index).trim();
      if (stripped.length >= 3) return stripped;
    }
  }
  return title;
};

/** Clean product title for display: strip brand prefix and trailing colour */
export const cleanTitle = (name, brand, colors) => {
  let title = stripBrand(name, brand);
  title = stripTrailingColour(title, colors);
  title = stripParenColour(title, colors);
  return title;
};

/** Calculate total real stock from stock_by_size, ignoring negative */
const getRealStock = (product) => {
  const sbs = product.stock_by_size;
  if (!sbs) return product.stock ?? 0;
  return Object.values(sbs).reduce((sum, v) => sum + Math.max(0, v), 0);
};

/* ─── Badges ─── */

const Badge = ({ label, variant = 'dark', testId }) => {
  const styles = {
    sale: 'bg-[#DEA193] text-white',
    dark: 'bg-[#111] text-white',
    low: 'bg-white/90 backdrop-blur-sm text-[#000] border border-[#E5D8D0]',
    out: 'bg-white/90 backdrop-blur-sm text-[#999] border border-[#E5D8D0]',
    bestseller: 'bg-[#C4A265] text-white',
  };
  return (
    <span
      className={`px-2 py-1 text-[10px] tracking-[0.15em] font-semibold uppercase inline-flex items-center gap-1 ${styles[variant]}`}
      data-testid={testId}
    >
      {variant === 'bestseller' && <Award className="w-3 h-3" aria-hidden="true" />}
      {variant === 'sale' && <Percent className="w-3 h-3" aria-hidden="true" />}
      {variant === 'low' && <AlertTriangle className="w-3 h-3" aria-hidden="true" />}
      {label}
    </span>
  );
};

const CardBadges = ({ product, isBestseller }) => {
  const realStock = getRealStock(product);
  const isSale = product.on_sale && product.sale_price > 0;
  const isLow = realStock > 0 && realStock <= 3;
  const isOut = realStock <= 0;

  if (!isSale && !isLow && !isOut && !isBestseller) return null;

  return (
    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5" data-testid="card-badges">
      {isBestseller && <Badge label="Bestseller" variant="bestseller" testId="bestseller-badge" />}
      {isSale && <Badge label="Sale" variant="sale" testId="sale-badge" />}
      {isOut && <Badge label="Sold Out" variant="out" testId="sold-out-badge" />}
      {!isOut && isLow && <Badge label="Low Stock" variant="low" testId="low-stock-badge" />}
    </div>
  );
};

/* ─── Price ─── */

const PriceDisplay = ({ price, onSale, salePrice, className = '' }) => {
  if (onSale && salePrice && salePrice < price) {
    const pct = Math.round((1 - salePrice / price) * 100);
    return (
      <div className={`flex items-center gap-2 ${className}`} data-testid="price-display">
        <span className="text-[#DEA193] font-medium">£{safePrice(salePrice)}</span>
        <span className="text-[#999] line-through text-xs">£{safePrice(price)}</span>
        <span className="text-[10px] text-[#DEA193] font-semibold">-{pct}%</span>
      </div>
    );
  }
  return <p className={`text-[#000] font-medium ${className}`} data-testid="price-display">£{safePrice(price)}</p>;
};

/* ─── Colour dots ─── */

const SWATCH_MAP = {
  'Black': '#000', 'White': '#FFF', 'Pink': '#F4A4B8', 'Light Pink': '#FFD1DC',
  'Pale Pink': '#FADADD', 'Candy Pink': '#E4717A', 'Theatrical Pink': '#F77FBE',
  'Ballet Pink Leather': '#E8B4B8', 'Blush': '#DE98AB', 'Rose': '#C67D7D',
  'Coral': '#F08080', 'Raspberry': '#C4295B', 'Red': '#CC2936', 'Burgundy': '#800020',
  'Black Cherry': '#49111C', 'Terracotta': '#C06040', 'Salted Caramel': '#C49560',
  'Caramel Blonde': '#C49560', 'Chocolate': '#5C3317', 'Brown': '#8B6F47',
  'Light Brown': '#B68D66', 'Med Brown': '#9A6F48', 'Wood': '#8B7355', 'Tan': '#D2B48C',
  'Taupe': '#B8A99A', 'Sand': '#D4C5A9', 'Nude': '#E3C9A8', 'Light Nude': '#F0DFC8',
  'Beige': '#F5E1C8', 'Natural': '#EEDCC8', 'Oat': '#E9DDD0', 'Cream': '#FFFDD0',
  'Milky Coconut': '#F5F0E8', 'Smooth Vanilla': '#F5E6CC', 'Blonde': '#F5DEB3',
  'Moonstone': '#BCC5CE', 'Slate': '#708090', 'Marl': '#A8A8A8',
  'Navy': '#1A2744', 'Deep Navy': '#0F1A2E', 'Royal': '#2B3A8E', 'Haze Blue': '#99B5C8',
  'Forest Green': '#2D5A27', 'Olive': '#708238', 'Soft Violet': '#A68BB5',
  'Leopard': 'pattern', 'Clear': 'transparent', 'clear': 'transparent',
};

const LIGHT_COLOURS = new Set(['White', 'Cream', 'Natural', 'Oat', 'Milky Coconut', 'Smooth Vanilla', 'Beige', 'Light Nude', 'Blonde', 'Clear', 'clear']);

const ColourDot = ({ colour }) => {
  const hex = SWATCH_MAP[colour];
  if (!hex) return null;
  if (hex === 'transparent') {
    return <span className="w-3 h-3 rounded-full border-2 border-dashed border-[#ccc] flex-shrink-0" title={colour} role="img" aria-label={colour} />;
  }
  if (hex === 'pattern') {
    return (
      <span
        className="w-3 h-3 rounded-full flex-shrink-0 border border-[#ddd]"
        style={{ background: 'radial-gradient(circle at 30% 30%, #3B2507 1.5px, transparent 1.5px), radial-gradient(circle at 70% 60%, #3B2507 1.5px, transparent 1.5px), linear-gradient(135deg, #D4A952, #C4913A)' }}
        title={colour}
        role="img" aria-label={colour}
      />
    );
  }
  const light = LIGHT_COLOURS.has(colour);
  return (
    <span
      className={`w-3 h-3 rounded-full flex-shrink-0 ${light ? 'border border-[#ddd]' : ''}`}
      style={{ backgroundColor: hex }}
      title={colour}
      role="img" aria-label={colour}
    />
  );
};

const ColourInfo = ({ colors }) => {
  if (!colors?.length) return null;
  return (
    <div className="flex items-center gap-1.5" data-testid="card-colours">
      {colors.slice(0, 5).map(c => <ColourDot key={c} colour={c} />)}
      {colors.length > 5 && <span className="text-[10px] text-[#999]">+{colors.length - 5}</span>}
      {colors.length <= 2 && (
        <span className="text-xs text-[#999]">{colors.join(', ')}</span>
      )}
    </div>
  );
};

/* ─── Wishlist ─── */

const WishlistButton = ({ productId, product }) => {
  const { isAuthenticated } = useAuth();
  const { isInWishlist, toggleWishlist } = useWishlist();
  if (!isAuthenticated) return null;

  const inList = isInWishlist(productId);
  return (
    <button
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const ok = await toggleWishlist(productId, product);
        if (ok) toast.success(inList ? 'Removed from wishlist' : 'Added to wishlist');
      }}
      className={`absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm border border-[#E5D8D0] hover:border-[#DEA193] transition-all z-10 ${inList ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
      data-testid={`wishlist-btn-${productId}`}
      aria-label={inList ? 'Remove from wishlist' : 'Add to wishlist'}
    >
      <Heart className={`w-4 h-4 transition-colors ${inList ? 'fill-[#DEA193] text-[#DEA193]' : 'text-[#737373]'}`} />
    </button>
  );
};

/* ─── Stars ─── */

const MiniStars = ({ rating, count }) => {
  if (!count || count === 0) return null;
  return (
    <div className="flex items-center gap-1.5" data-testid="product-rating">
      <div className="flex gap-px">
        {[1, 2, 3, 4, 5].map(s => (
          <Star key={s} className={`w-3 h-3 ${s <= Math.round(rating) ? 'fill-[#DEA193] text-[#DEA193]' : 'fill-none text-[#E5D8D0]'}`} />
        ))}
      </div>
      <span className="text-[10px] text-[#999]">({count})</span>
    </div>
  );
};

/* ─── Image with hover swap ─── */

const CardImage = ({ product }) => {
  const [hovered, setHovered] = useState(false);
  const hasSecond = product.images?.length >= 2;
  const primary = product.images?.[0] || '';
  const secondary = hasSecond ? product.images[1] : '';
  const isOut = getRealStock(product) <= 0;

  return (
    <div
      className={`aspect-[4/5] overflow-hidden bg-[#F5F5F5] relative ${isOut ? 'opacity-60' : ''}`}
      onMouseEnter={() => hasSecond && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="card-image-container"
    >
      {primary ? (
        <>
          <OptimizedImage
            src={primary}
            alt={`${product.brand ? product.brand + ' — ' : ''}${product.name}`}
            className={`w-full h-full object-contain transition-opacity duration-300 ${hovered ? 'opacity-0' : 'opacity-100'}`}
            width={480}
            sizes="(min-width: 1024px) 33vw, 50vw"
          />
          {hovered && secondary && (
            <OptimizedImage
              src={secondary}
              alt={`${product.brand ? product.brand + ' — ' : ''}${product.name} — alternate view`}
              className="absolute inset-0 w-full h-full object-contain"
              width={480}
              sizes="(min-width: 1024px) 33vw, 50vw"
            />
          )}
        </>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center bg-[#F0EFE7]">
          <div className="w-14 h-14 mb-3 rounded-full bg-[#E5D8D0]/30 flex items-center justify-center">
            <svg className="w-7 h-7 text-[#C4B5A8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
            </svg>
          </div>
          <span className="text-[11px] tracking-[0.1em] text-[#B0A89E] uppercase">Image Coming Soon</span>
        </div>
      )}
    </div>
  );
};

/* ─── Quick Add ─── */

const isSingleVariant = (product) => {
  const sizes = product.sizes || [];
  const colors = product.colors || [];
  if (sizes.length <= 1 && colors.length <= 1) return true;
  return false;
};

const getSizeStock = (product, size) => {
  const sbs = product.stock_by_size;
  if (sbs && sbs[size] !== undefined) return Math.max(0, sbs[size]);
  return product.stock ?? 0;
};

const QuickAdd = ({ product }) => {
  const { addToCart } = useCart();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const ref = useRef(null);
  const totalStock = getRealStock(product);
  const isOut = totalStock <= 0;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const doAdd = async (size, color) => {
    setAdding(true);
    const s = size || (product.sizes?.[0] ?? null);
    const c = color || (product.colors?.[0] ?? null);
    const result = await addToCart(product.id, 1, s, c);
    if (result.success) {
      setAdded(true);
      setOpen(false);
      setTimeout(() => setAdded(false), 1800);
    } else {
      toast.error(result.error || 'Could not add to bag');
    }
    setAdding(false);
  };

  if (isOut) {
    return (
      <button disabled className="w-full mt-2 py-2 text-[11px] tracking-[0.1em] text-[#999] border border-[#E5D8D0] cursor-not-allowed" data-testid="quick-add-sold-out">
        Sold Out
      </button>
    );
  }

  if (added) {
    return (
      <div className="w-full mt-2 py-2 flex items-center justify-center gap-1.5 text-[11px] tracking-[0.1em] text-[#DEA193] border border-[#DEA193] bg-[#DEA193]/5" data-testid="quick-add-confirmed">
        <Check className="w-3.5 h-3.5" /> Added
      </div>
    );
  }

  /* Single variant — instant add */
  if (isSingleVariant(product)) {
    return (
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); doAdd(); }}
        disabled={adding}
        className="w-full mt-2 py-2 flex items-center justify-center gap-1.5 text-[11px] tracking-[0.1em] font-medium border border-[#E5D8D0] hover:border-[#000] hover:bg-[#000] hover:text-white transition-colors disabled:opacity-50"
        data-testid="quick-add-btn"
      >
        <ShoppingBag className="w-3.5 h-3.5" /> {adding ? 'Adding...' : 'Add to Bag'}
      </button>
    );
  }

  /* Multi-variant — size picker */
  const sizes = product.sizes || [];
  return (
    <div ref={ref} className="relative mt-2">
      {!open ? (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
          className="w-full py-2 flex items-center justify-center gap-1.5 text-[11px] tracking-[0.1em] font-medium border border-[#E5D8D0] hover:border-[#000] hover:bg-[#000] hover:text-white transition-colors"
          data-testid="quick-add-select-size"
        >
          <ShoppingBag className="w-3.5 h-3.5" /> Select Size <ChevronDown className="w-3 h-3" />
        </button>
      ) : (
        <div
          className="flex flex-wrap gap-1 p-1.5 border border-[#000] bg-white animate-in fade-in slide-in-from-bottom-2 duration-150"
          data-testid="quick-add-sizes"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          {sizes.map(size => {
            const stock = getSizeStock(product, size);
            const oos = stock <= 0;
            return (
              <button
                key={size}
                disabled={oos || adding}
                onClick={() => !oos && doAdd(size)}
                className={`flex-1 min-w-[36px] py-1.5 text-[11px] font-medium transition-colors ${
                  oos
                    ? 'text-[#ccc] line-through cursor-not-allowed'
                    : 'hover:bg-[#000] hover:text-white border border-[#E5D8D0] hover:border-[#000]'
                }`}
                data-testid={`quick-add-size-${size}`}
                aria-label={oos ? `Size ${size} — sold out` : `Add size ${size} to bag`}
              >
                {size}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   ProductCard — main card used everywhere
   ═══════════════════════════════════════════════════ */

export const ProductCard = ({ product, onQuickView }) => {
  const title = cleanTitle(product.name, product.brand, product.colors);
  const { ids: bestsellerIds } = useBestsellers();
  const isBestseller = bestsellerIds.has(product.id);

  return (
    <div className="product-card group block relative" data-testid={`product-card-${product.id}`}>
      <WishlistButton productId={product.id} product={product} />
      {onQuickView && <QuickViewButton onClick={() => onQuickView(product)} />}
      <CardBadges product={product} isBestseller={isBestseller} />
      <Link to={`/product/${product.id}`}>
        <CardImage product={product} />
        <div className="pt-3 space-y-1">
          <p className="text-[11px] tracking-[0.15em] text-[#999] uppercase" data-testid="card-brand">
            {product.brand || product.category}
          </p>
          <h3 className="text-sm text-[#000] leading-snug group-hover:text-[#DEA193] transition-colors line-clamp-2" data-testid="card-title">
            {title}
          </h3>
          <ColourInfo colors={product.colors} />
          <MiniStars rating={product.average_rating} count={product.review_count} />
          <PriceDisplay price={product.price} onSale={product.on_sale} salePrice={product.sale_price} className="text-sm" />
        </div>
      </Link>
      <QuickAdd product={product} />
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   FeaturedProductCard — homepage / hero sections
   ═══════════════════════════════════════════════════ */

export const FeaturedProductCard = ({ product }) => {
  const title = cleanTitle(product.name, product.brand, product.colors);
  const { ids: bestsellerIds } = useBestsellers();
  const isBestseller = bestsellerIds.has(product.id);

  return (
    <div className="product-card group block relative" data-testid={`featured-product-${product.id}`}>
      <WishlistButton productId={product.id} product={product} />
      <CardBadges product={product} isBestseller={isBestseller} />
      <Link to={`/product/${product.id}`}>
        <CardImage product={product} />
        <div className="pt-4 space-y-1.5">
          <p className="text-[11px] tracking-[0.2em] text-[#999] uppercase">{product.brand || product.category}</p>
          <h3 className="text-lg text-[#000] leading-snug group-hover:text-[#DEA193] transition-colors line-clamp-2">{title}</h3>
          <ColourInfo colors={product.colors} />
          <MiniStars rating={product.average_rating} count={product.review_count} />
          <PriceDisplay price={product.price} onSale={product.on_sale} salePrice={product.sale_price} />
        </div>
      </Link>
    </div>
  );
};
